import type { FC } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';

import { useGetMeinOrtPosts } from '@/features/api/hooks/grapevine-post/useGetMeinOrtPosts';
import { FeatureGuard } from '@/features/feature-management/components/FeatureGuard';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import {
  MeinOrtChannel,
  MeinOrtTab
} from '@/features/mein-ort/components/meinOrtTypes';
import { useGetMeinOrtPostsParams } from '@/features/posts/hooks/useGetPostsParams';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { SectionContent } from '@/features/start/components/components/SectionContent';
import { HappeningCard } from '@/features/start/components/radio/HappeningCard';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';
import { useRefetchOnFocus } from '@/shared/hooks/useRefetchOnFocus';
import { hasItems } from '@/shared/utils/array';

const styles = StyleSheet.create({
  loading: { paddingVertical: 24, alignItems: 'center' }
});

export const MeinOrtHappeningsSection: FC = memo(() => {
  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'screens.start.sections.radio'
  });

  const happeningParams = useGetMeinOrtPostsParams(PostFeedType.HAPPENING);
  const happeningQuery = useGetMeinOrtPosts({
    ...happeningParams,
    count: 1
  });
  const { data: happenings, isLoading } = happeningQuery;
  useRefetchOnFocus(happeningQuery);

  if (!isLoading && !hasItems(happenings)) {
    return;
  }

  return (
    <FeatureGuard features={[FeaturesEnum.Happening]}>
      <SectionContent
        title={t('happenings')}
        testId="section-mein-ort-happenings"
        screen={AppRoute.Tabs}
        params={{
          screen: TabRoute.Radio,
          params: {
            channel: MeinOrtChannel.FUNKZENTRALE,
            tab: MeinOrtTab.EVENTS
          }
        }}
      >
        {isLoading ? (
          <View style={styles.loading}>
            <ActivityIndicator size="large" />
          </View>
        ) : (
          happenings?.map((happening) => (
            <HappeningCard key={happening.postId} {...happening} />
          ))
        )}
      </SectionContent>
    </FeatureGuard>
  );
});

MeinOrtHappeningsSection.displayName = 'MeinOrtHappeningsSection';
